import { ExternalServiceId, InternalId, StaticReference, StaticRevisionReference } from '@edgebox/data-definition-kit';
import {
  ContentOptimizationStatus,
  ContentOptimizationTargetStage,
  ContentRepresentation,
  SyndicationStatus,
  TaskDomain,
  TaskStatus,
} from '@edgebox/sync-core-data-definitions';
import {
  ClientContentItemEntity,
  ClientContentOptimizationDraft,
  ClientContentOptimizationTypeEntity,
  ClientTaskEntity,
  ISyncCoreApi,
} from '@edgebox/sync-core-rest-client';
import { Button } from '@edgebox/react-components';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { SyndicationStatusIcon } from '../SyndicationStatusIcon';
import { BrandPresencePageHeader } from './Components/BrandPresencePageHeader';
import { HealthScoreCell } from './Components/HealthScoreCell';
import { SkeletonBlock, SkeletonRegion } from './Components/BrandPresenceSkeleton';
import { BP_CARD_CLASSES, BP_TABLE_HEAD_CELL_CLASSES } from './Components/brandPresenceClasses';

function decodeTypeParam(rawType?: string): string {
  if (!rawType) {
    return '';
  }

  try {
    return decodeURIComponent(rawType);
  } catch {
    return rawType;
  }
}

async function loadOptimizationTypeName(api: ISyncCoreApi | undefined, typeKey: string): Promise<string> {
  if (!api || !typeKey) {
    return typeKey;
  }

  const item = await api.governance.contentOptimizationTypes.itemByKey(typeKey as ExternalServiceId);
  return item?.name || typeKey;
}

async function loadItemFromOptionalReference<T>(reference: { get: () => Promise<T> } | undefined): Promise<T | undefined> {
  if (!reference) {
    return undefined;
  }

  return reference.get().catch(() => undefined);
}

function parseSelectedKeys(search: string): string[] {
  const params = new URLSearchParams(search.replace(/^\?/, ''));
  const raw = params.get('keys');
  if (!raw) {
    return [];
  }

  return raw
    .split(',')
    .map((key) => key.trim())
    .filter((key) => key.length > 0);
}

function toDomain(urlValue?: string): string | undefined {
  if (!urlValue) {
    return undefined;
  }

  try {
    return new URL(urlValue).hostname;
  } catch {
    return undefined;
  }
}

function toPath(urlValue?: string): string {
  if (!urlValue) {
    return '/';
  }

  try {
    const parsed = new URL(urlValue);
    return `${parsed.pathname || '/'}${parsed.search || ''}`;
  } catch {
    return '/';
  }
}

function toScorePercentage(score?: number): number | undefined {
  if (score === undefined || !Number.isFinite(score)) {
    return undefined;
  }

  return Math.round(Math.max(0, Math.min(1, score)) * 100);
}

type OptimizeTaskRow = {
  id: string;
  name: string;
  siteDomain?: string;
  path: string;
  status: SyndicationStatus;
  healthScore?: number;
};

function isProcessingTaskStatus(status?: string): boolean {
  return !!status?.includes(TaskStatus.Initializing) || !!status?.includes(TaskStatus.Running) || !!status?.includes(TaskStatus.Retrying);
}

async function resolveTaskRow(task: ClientTaskEntity): Promise<OptimizeTaskRow> {
  const optimization = task.contentOptimization ? await task.contentOptimization.get().catch(() => undefined) : undefined;

  const inputFromOptimization = optimization
    ? (await loadItemFromOptionalReference(optimization.inputPage)) || (await loadItemFromOptionalReference(optimization.inputEntity))
    : undefined;

  const contentItem = task.contentItem ? await task.contentItem.get().catch(() => undefined) : inputFromOptimization;
  const outputFromOptimization = optimization
    ? (await loadItemFromOptionalReference(optimization.outputPage)) || (await loadItemFromOptionalReference(optimization.outputEntity))
    : undefined;

  const site = contentItem?.site ? await contentItem.site.get().catch(() => undefined) : undefined;

  return {
    id: task.id,
    name: contentItem?.name || contentItem?.key || `Task ${task.id}`,
    siteDomain: toDomain(contentItem?.url) || toDomain(site?.baseUrl),
    path: toPath(contentItem?.url),
    status: task.status as SyndicationStatus,
    healthScore: toScorePercentage(outputFromOptimization?.healthScore),
  };
}

async function buildOptimizationDrafts(params: {
  items: ClientContentItemEntity[];
  optimizationType: ClientContentOptimizationTypeEntity | undefined;
}): Promise<ClientContentOptimizationDraft[]> {
  const { items, optimizationType } = params;

  if (!optimizationType) {
    return [];
  }

  const drafts = await Promise.all(
    items.map(async (item) => {
      const site = item.site ? await item.site.get().catch(() => undefined) : undefined;
      if (!site) {
        return undefined;
      }

      const draft = new ClientContentOptimizationDraft();
      draft.targetStage = ContentOptimizationTargetStage.Published;
      draft.status = ContentOptimizationStatus.Generating;
      draft.site = new StaticReference(site);
      draft.types = [new StaticRevisionReference(optimizationType)];
      draft.fixesIssueTypes = [];
      draft.fixesIssues = [];

      if (item.type === ContentRepresentation.Page) {
        draft.inputPage = new StaticRevisionReference(item);
      } else {
        draft.inputEntity = new StaticRevisionReference(item);
      }

      return draft;
    })
  );

  return drafts.filter((draft): draft is ClientContentOptimizationDraft => !!draft);
}

/** Result rows drawn while the pages of the optimization load: name, path, status, health. */
const RESULT_SKELETON_ROWS = [0, 1, 2, 3, 4].map((row) => (
  <div key={row} aria-hidden="true" className="flex items-center gap-6 px-4 py-4">
    <SkeletonBlock className={`h-4 ${row % 2 ? 'w-40' : 'w-56'}`} />
    <SkeletonBlock className="h-3.5 w-64" />
    <SkeletonBlock className="h-5 w-20 rounded-full" />
    <SkeletonBlock className="ml-auto h-4 w-16" />
  </div>
));

export function GovernanceOptimizeType() {
  const { type, taskGroupId } = useParams<{ type: string; taskGroupId?: string }>();
  const apiContext = React.useContext(SyncCoreApiContext);
  const api = apiContext?.api;
  const location = useLocation();
  const navigate = useNavigate();
  const typeKey = React.useMemo(() => decodeTypeParam(type), [type]);
  const selectedKeys = React.useMemo(() => parseSelectedKeys(location.search), [location.search]);
  const [title, setTitle] = React.useState(typeKey);
  const [optimizationType, setOptimizationType] = React.useState<ClientContentOptimizationTypeEntity | undefined>(undefined);
  const [selectedItems, setSelectedItems] = React.useState<ClientContentItemEntity[]>([]);
  const [loadingSelectedItems, setLoadingSelectedItems] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | undefined>(undefined);
  const [rows, setRows] = React.useState<OptimizeTaskRow[]>([]);
  const [loadingRows, setLoadingRows] = React.useState(false);
  const [rowsError, setRowsError] = React.useState<string | undefined>(undefined);
  const [taskGroupLabel, setTaskGroupLabel] = React.useState<string>('');
  const [taskGroupStatus, setTaskGroupStatus] = React.useState<string | undefined>(undefined);

  const taskGroupPollingIntervalRef = React.useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const taskGroupPollingRequestInFlightRef = React.useRef(false);

  React.useEffect(() => {
    let active = true;
    void (async () => {
      if (!api || !typeKey) {
        return;
      }

      const loadedType = await api.governance.contentOptimizationTypes.itemByKey(typeKey as ExternalServiceId);
      if (!active) {
        return;
      }

      setOptimizationType(loadedType || undefined);
      setTitle(loadedType?.name || (await loadOptimizationTypeName(api, typeKey)));
    })();

    return () => {
      active = false;
    };
  }, [api, typeKey]);

  React.useEffect(() => {
    if (!api || !typeKey || !!taskGroupId) {
      return;
    }

    let active = true;
    setLoadingSelectedItems(true);
    setSubmitError(undefined);

    void (async () => {
      try {
        const loadedItems = await Promise.all(selectedKeys.map((key) => api.governance.contentItems.itemByKey(key, undefined, true)));
        if (!active) {
          return;
        }

        setSelectedItems(loadedItems.filter((item): item is ClientContentItemEntity => !!item));
      } catch (error) {
        if (!active) {
          return;
        }

        setSubmitError((error as Error)?.message || 'Unable to load selected content items.');
      } finally {
        if (active) {
          setLoadingSelectedItems(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api, selectedKeys, taskGroupId, typeKey]);

  const refreshTaskGroupDetails = React.useCallback(
    async (options?: { silent?: boolean }): Promise<void> => {
      if (!api || !taskGroupId) {
        return;
      }

      if (taskGroupPollingRequestInFlightRef.current) {
        return;
      }

      const silent = options?.silent ?? false;

      taskGroupPollingRequestInFlightRef.current = true;
      if (!silent) {
        setLoadingRows(true);
      }

      try {
        const [taskGroup, tasksResponse] = await Promise.all([
          api.service.taskGroups.itemWithSummary(taskGroupId as InternalId, true),
          api.service.tasks.list(
            { domains: [TaskDomain.Governance], migrationId: taskGroupId as InternalId },
            { page: 0, itemsPerPage: 500 }
          ),
        ]);

        const loadedRows = await Promise.all(tasksResponse.items.map((task) => resolveTaskRow(task)));

        setTaskGroupLabel(`Task group #${taskGroup.id}`);
        setTaskGroupStatus(taskGroup.status);
        setRows(loadedRows);
        setRowsError(undefined);
      } catch (error) {
        setRowsError((error as Error)?.message || 'Unable to load optimization task group details.');
      } finally {
        taskGroupPollingRequestInFlightRef.current = false;
        if (!silent) {
          setLoadingRows(false);
        }
      }
    },
    [api, taskGroupId]
  );

  React.useEffect(() => {
    if (!taskGroupId) {
      return;
    }

    void refreshTaskGroupDetails();
  }, [refreshTaskGroupDetails, taskGroupId]);

  const shouldPollTaskGroup = React.useMemo(() => {
    if (!taskGroupId) {
      return false;
    }

    return isProcessingTaskStatus(taskGroupStatus);
  }, [taskGroupId, taskGroupStatus]);

  React.useEffect(() => {
    if (shouldPollTaskGroup) {
      if (!taskGroupPollingIntervalRef.current) {
        taskGroupPollingIntervalRef.current = setInterval(() => {
          void refreshTaskGroupDetails({ silent: true });
        }, 5000);
      }

      return () => {
        if (taskGroupPollingIntervalRef.current) {
          clearInterval(taskGroupPollingIntervalRef.current);
          taskGroupPollingIntervalRef.current = undefined;
        }
      };
    }

    if (taskGroupPollingIntervalRef.current) {
      clearInterval(taskGroupPollingIntervalRef.current);
      taskGroupPollingIntervalRef.current = undefined;
    }

    return () => {
      if (taskGroupPollingIntervalRef.current) {
        clearInterval(taskGroupPollingIntervalRef.current);
        taskGroupPollingIntervalRef.current = undefined;
      }
    };
  }, [refreshTaskGroupDetails, shouldPollTaskGroup]);

  const siteColumnVisible = React.useMemo(() => {
    const uniqueDomains = new Set(rows.map((row) => row.siteDomain || '-'));
    return uniqueDomains.size > 1;
  }, [rows]);

  const onOptimize = React.useCallback(async () => {
    if (!api || !typeKey || !optimizationType) {
      return;
    }

    setSubmitting(true);
    setSubmitError(undefined);

    try {
      const drafts = await buildOptimizationDrafts({ items: selectedItems, optimizationType });
      if (!drafts.length) {
        throw new Error('No valid content items available for optimization.');
      }

      const response = await api.governance.contentOptimizations.createMany(drafts);
      navigate(`/brand-presence/optimize/${encodeURIComponent(typeKey)}/${response.taskGroup.id}`);
    } catch (error) {
      setSubmitError((error as Error)?.message || 'Unable to start optimization.');
    } finally {
      setSubmitting(false);
    }
  }, [api, navigate, optimizationType, selectedItems, typeKey]);

  if (taskGroupId) {
    return (
      <div className="relative w-full h-full bg-slate-50 text-slate-900 bg-brand-page">
        <main className="relative max-w-[1200px] min-h-[600px] mx-auto px-6 py-8 space-y-4">
          <BrandPresencePageHeader
            eyebrow={
              <>
                Brand Presence <span className="text-slate-300">/</span> Optimize
              </>
            }
            title={title || 'Optimization'}
            description={taskGroupLabel || 'Optimization task group'}
          />

          {/* The results table scrolls sideways inside the card where it is wider than the screen. */}
          <div className={`${BP_CARD_CLASSES} overflow-x-auto motion-safe:animate-bp-fade-up`}>
            {loadingRows ? (
              <SkeletonRegion label="Loading optimization results…" className="divide-y divide-slate-100">
                {RESULT_SKELETON_ROWS}
              </SkeletonRegion>
            ) : null}
            {!loadingRows && rowsError ? <div className="px-6 py-5 text-sm text-danger">{rowsError}</div> : null}
            {!loadingRows && !rowsError ? (
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200/70 bg-slate-50/70">
                  <tr>
                    <th className={BP_TABLE_HEAD_CELL_CLASSES}>Name</th>
                    {siteColumnVisible ? <th className={BP_TABLE_HEAD_CELL_CLASSES}>Site</th> : null}
                    <th className={BP_TABLE_HEAD_CELL_CLASSES}>Path</th>
                    <th className={BP_TABLE_HEAD_CELL_CLASSES}>Status</th>
                    <th className={BP_TABLE_HEAD_CELL_CLASSES}>Health</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => (
                    <tr key={row.id} className="align-middle transition-colors duration-150 hover:bg-slate-50/80">
                      <td className="px-4 py-3.5 font-semibold text-slate-900">{row.name}</td>
                      {siteColumnVisible ? <td className="px-4 py-3.5 text-slate-600">{row.siteDomain || '-'}</td> : null}
                      <td className="px-4 py-3.5 font-eyebrow text-[0.8125rem] text-slate-500 max-w-[28rem] truncate" title={row.path}>
                        {row.path}
                      </td>
                      <td className="px-4 py-3.5 text-slate-700">
                        <span className="inline-flex items-center gap-2">
                          <SyndicationStatusIcon status={row.status} size="sm" />
                          <span>{row.status}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <HealthScoreCell score={row.healthScore} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-slate-50 text-slate-900 bg-brand-page">
      <main className="relative max-w-[1200px] min-h-[600px] mx-auto px-6 py-8 space-y-4">
        <BrandPresencePageHeader
          eyebrow={
            <>
              Brand Presence <span className="text-slate-300">/</span> Optimize
            </>
          }
          title={title || 'Optimization'}
        />
        <div className={`${BP_CARD_CLASSES} space-y-2 p-6 motion-safe:animate-bp-fade-up`}>
          <p className="m-0 text-[0.9375rem] text-slate-600">
            Apply this optimization type to {loadingSelectedItems ? '...' : selectedItems.length} selected content item
            {selectedItems.length === 1 ? '' : 's'}.
          </p>
          {submitError ? <div className="text-sm text-danger">{submitError}</div> : null}
          <div className="d-flex gap-2 pt-2">
            <Button variant="outline-secondary" disabled={submitting} onClick={() => navigate('/brand-presence/content-inventory')}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={submitting || loadingSelectedItems || selectedItems.length === 0 || !optimizationType}
              onClick={() => void onOptimize()}
            >
              <span className="inline-flex items-center gap-2">
                {submitting ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
                <span>{submitting ? 'Optimizing…' : 'Optimize'}</span>
              </span>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
